import * as THREE from 'three';
import { MeshBVH } from 'three-mesh-bvh';

/** Tuning is per 60 Hz tick, so the feel doesn't change with frame rate. */
export const MOVE = {
  accel: 0.0058,
  sprint: 1.35,
  gravity: -0.0105,
  jump: 0.16,
  damp: 0.9,
  dampIdle: 0.62,
  radius: 0.27,
  height: 1.25,
  floorDot: 0.62,
  turnLerp: 0.16,
  coyoteMs: 70,
  jumpBufferMs: 110,
};

export type MoveInput = { x: number; y: number; sprint: boolean; jump: boolean };

const tmpBox = new THREE.Box3();
const tmpSeg = new THREE.Line3();
const triPt = new THREE.Vector3();
const capPt = new THREE.Vector3();
const v1 = new THREE.Vector3();
const v2 = new THREE.Vector3();
const m4 = new THREE.Matrix4();

export class PlayerController {
  readonly position = new THREE.Vector3();
  readonly prevPosition = new THREE.Vector3();
  readonly velocity = new THREE.Vector3();
  /** Unit vector the character faces, always tangent to the planet. */
  readonly facing = new THREE.Vector3(0, 0, 1);
  readonly prevFacing = new THREE.Vector3(0, 0, 1);
  readonly up = new THREE.Vector3(0, 1, 0);
  onFloor = false;
  inAir = false;
  frozen = false;
  /** Multiplies walking speed (e.g. 0.6 while carrying a heavy crate). */
  speedScale = 1;
  private lastFloorTime = 0;
  private jumpRequestTime = -1e9;
  private readonly bvh: MeshBVH;
  private readonly onLand: () => void;
  private readonly onJump: () => void;

  constructor(collider: THREE.Mesh, spawn: THREE.Vector3, hooks: { land?: () => void; jump?: () => void } = {}) {
    this.bvh = new MeshBVH(collider.geometry);
    this.position.copy(spawn);
    this.prevPosition.copy(spawn);
    this.up.copy(spawn).normalize();
    this.facing.set(1, 0, 0).projectOnPlane(this.up).normalize();
    this.prevFacing.copy(this.facing);
    this.onLand = hooks.land ?? (() => {});
    this.onJump = hooks.jump ?? (() => {});
  }

  get speed(): number {
    return v1.copy(this.velocity).projectOnPlane(this.up).length() * 60;
  }

  requestJump(now: number): void {
    this.jumpRequestTime = now;
  }

  /**
   * One physics tick. `camForward` is the camera's forward direction, which
   * makes "W" mean "into the screen" wherever you are on the planet.
   */
  tick(input: MoveInput, camForward: THREE.Vector3, now: number): void {
    this.prevPosition.copy(this.position);
    this.prevFacing.copy(this.facing);
    this.up.copy(this.position).normalize();
    const up = this.up;

    const fwd = v1.copy(camForward).projectOnPlane(up);
    if (fwd.lengthSq() < 1e-8) fwd.copy(this.facing);
    fwd.normalize();
    const right = v2.crossVectors(fwd, up).normalize();

    const wish = new THREE.Vector3().addScaledVector(fwd, input.y).addScaledVector(right, input.x);
    const amount = Math.min(1, wish.length());
    const moving = amount > 0.01 && !this.frozen;
    if (moving) {
      wish.normalize();
      this.velocity.addScaledVector(wish, MOVE.accel * amount * this.speedScale * (input.sprint && this.speedScale >= 1 ? MOVE.sprint : 1));
      // Turn toward where we're heading, quicker when the turn is small.
      this.facing.lerp(wish, MOVE.turnLerp).projectOnPlane(up).normalize();
    }

    const canJump = now - this.lastFloorTime < MOVE.coyoteMs;
    if (!this.frozen && now - this.jumpRequestTime < MOVE.jumpBufferMs && canJump) {
      this.velocity.addScaledVector(up, MOVE.jump);
      this.jumpRequestTime = -1e9;
      this.lastFloorTime = -1e9;
      this.onJump();
    }

    this.velocity.addScaledVector(up, MOVE.gravity);
    // Damp only the sideways part so falls keep their weight.
    const vertical = this.velocity.dot(up);
    this.velocity.addScaledVector(up, -vertical);
    this.velocity.multiplyScalar(moving || !this.onFloor ? MOVE.damp : MOVE.dampIdle);
    this.velocity.addScaledVector(up, vertical);

    const wasAir = this.inAir;
    for (let i = 0; i < 3; i++) {
      this.position.addScaledVector(this.velocity, 1 / 3);
      this.resolveCollisions();
    }
    if (this.onFloor) this.lastFloorTime = now;
    this.inAir = !this.groundWithin(0.22);
    if (wasAir && !this.inAir) this.onLand();
  }

  /** Interpolated pose between the last two physics ticks. */
  sample(alpha: number, outPos: THREE.Vector3, outQuat: THREE.Quaternion): void {
    outPos.lerpVectors(this.prevPosition, this.position, alpha);
    const up = v1.copy(outPos).normalize();
    const f = v2.lerpVectors(this.prevFacing, this.facing, alpha).projectOnPlane(up).normalize();
    const x = new THREE.Vector3().crossVectors(up, f).normalize();
    m4.makeBasis(x, up, f);
    outQuat.setFromRotationMatrix(m4);
  }

  private resolveCollisions(): void {
    const up = this.up;
    const r = MOVE.radius;
    tmpSeg.start.copy(this.position).addScaledVector(up, r);
    tmpSeg.end.copy(this.position).addScaledVector(up, MOVE.height - r);
    tmpBox.makeEmpty().expandByPoint(tmpSeg.start).expandByPoint(tmpSeg.end).expandByScalar(r);

    const before = tmpSeg.start.clone();
    this.bvh.shapecast({
      intersectsBounds: (box) => box.intersectsBox(tmpBox),
      intersectsTriangle: (tri) => {
        const d = tri.closestPointToSegment(tmpSeg, triPt, capPt);
        if (d < r) {
          const push = capPt.sub(triPt).normalize().multiplyScalar(r - d);
          tmpSeg.start.add(push);
          tmpSeg.end.add(push);
        }
      },
    });
    const delta = tmpSeg.start.sub(before);
    const len = delta.length();
    this.onFloor = false;
    if (len > 1e-6) {
      const n = delta.divideScalar(len);
      // Pushed mostly upward means we're standing on something walkable.
      if (n.dot(up) > MOVE.floorDot) this.onFloor = true;
      this.position.addScaledVector(n, len);
      const into = this.velocity.dot(n);
      if (into < 0) this.velocity.addScaledVector(n, -into);
    }
  }

  private groundWithin(dist: number): boolean {
    const ray = new THREE.Ray(this.position.clone().addScaledVector(this.up, 0.05), this.up.clone().negate());
    const hit = this.bvh.raycastFirst(ray, THREE.DoubleSide);
    return !!hit && hit.distance < dist + 0.05;
  }

  /** Ray against the world collider (used by the camera). */
  raycast(origin: THREE.Vector3, dir: THREE.Vector3, far: number): number | null {
    const hit = this.bvh.raycastFirst(new THREE.Ray(origin, dir), THREE.DoubleSide);
    return hit && hit.distance <= far ? hit.distance : null;
  }
}
